import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 10,
    fontFamily: 'Helvetica',
    backgroundColor: '#ffffff',
  },
  dentalHubHeader: {
    marginBottom: 20,
    paddingBottom: 15,
    borderBottom: '2 solid #000000',
    flexDirection: 'column',
    alignItems: 'center',
  },
  dentalHubLogo: {
    width: 120,
    height: 60,
    marginBottom: 5,
    objectFit: 'contain',
  },
  dentalHubInfo: {
    width: '100%',
    alignItems: 'center',
    flexDirection: 'column',
  },
  dentalHubContact: {
    fontSize: 9,
    color: '#666666',
    marginBottom: 2,
    textAlign: 'center',
  },
  header: {
    marginBottom: 30,
    paddingBottom: 15,
    borderBottom: '2 solid #000000',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'center',
  },
  invoiceInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  infoBox: {
    width: '48%',
  },
  label: {
    fontSize: 9,
    color: '#666666',
    marginBottom: 3,
  },
  value: {
    fontSize: 11,
    fontWeight: 'bold',
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 10,
    paddingBottom: 5,
    borderBottom: '1 solid #cccccc',
  },
  row: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  rowLabel: {
    width: '40%',
    fontSize: 10,
    color: '#666666',
  },
  rowValue: {
    width: '60%',
    fontSize: 10,
    fontWeight: 'bold',
  },
  table: {
    marginTop: 10,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f0f0f0',
    padding: 8,
    borderBottom: '1 solid #cccccc',
  },
  tableRow: {
    flexDirection: 'row',
    padding: 8,
    borderBottom: '1 solid #eeeeee',
  },
  tableCell: {
    fontSize: 10,
  },
  tableCellRight: {
    fontSize: 10,
    textAlign: 'right',
  },
  totalSection: {
    marginTop: 20,
    paddingTop: 15,
    borderTop: '2 solid #000000',
    alignItems: 'flex-end',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 5,
    width: '40%',
  },
  totalLabel: {
    fontSize: 11,
    width: '60%',
    textAlign: 'right',
    paddingRight: 10,
  },
  totalValue: {
    fontSize: 11,
    fontWeight: 'bold',
    width: '40%',
    textAlign: 'right',
  },
  grandTotal: {
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: 10,
  },
  footer: {
    marginTop: 20,
    paddingTop: 15,
    borderTop: '1 solid #cccccc',
    fontSize: 9,
    color: '#666666',
    textAlign: 'center',
  },
});

const InvoicePDF = ({ invoiceData }) => {
  const {
    invoice_number,
    invoice_date,
    hospital_name,
    hospital_email,
    hospital_phone,
    hospital_address,
    plan_type,
    payment_plan,
    amount,
    subscription_start,
    subscription_end,
  } = invoiceData;

  // Dental Hub contact information
  const dentalHubEmail = 'neinususa@gmail.com'; // Dental Hub admin email
  const dentalHubPhone = '+91 1234567890'; // Update with actual phone number
  // Logo path - using full URL for react-pdf compatibility
  // File is located at: public/logo-white.png
  const dentalHubLogo = typeof window !== 'undefined' 
    ? `${window.location.origin}/logo-white.png`
    : '/logo-white.png'; // Fallback for SSR

  const planName = plan_type === 'SILVER' ? 'Silver Plan' : 'Gold Plan';
  const billingPeriod = payment_plan === 'MONTHLY' ? 'Monthly' : 'Yearly';
  
  // Calculate GST (18%)
  const subtotal = amount;
  const gstRate = 0.18;
  const gstAmount = subtotal * gstRate;
  const totalAmount = subtotal + gstAmount;
  
  const formattedSubtotal = `₹${subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formattedGst = `₹${gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formattedTotal = `₹${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  
  const invoiceDateFormatted = new Date(invoice_date).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const startDateFormatted = subscription_start
    ? new Date(subscription_start).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'N/A';
  const endDateFormatted = subscription_end
    ? new Date(subscription_end).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'N/A';

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Dental Hub Header */}
        <View style={styles.dentalHubHeader}>
          <Image src={dentalHubLogo} style={styles.dentalHubLogo} />
          <View style={styles.dentalHubInfo}>
            <Text style={styles.dentalHubContact}>Email: {dentalHubEmail}</Text>
            <Text style={styles.dentalHubContact}>Phone: {dentalHubPhone}</Text>
          </View>
        </View>

        {/* Invoice Header */}
        <View style={styles.header}>
          <Text style={styles.title}>INVOICE</Text>
        </View>

        {/* Invoice Info */}
        <View style={styles.invoiceInfo}>
          <View style={styles.infoBox}>
            <Text style={styles.label}>Invoice Number:</Text>
            <Text style={styles.value}>{invoice_number}</Text>
            <Text style={styles.label}>Invoice Date:</Text>
            <Text style={styles.value}>{invoiceDateFormatted}</Text>
          </View>
          <View style={styles.infoBox}>
            <Text style={styles.label}>Bill To:</Text>
            <Text style={styles.value}>{hospital_name}</Text>
            {hospital_address && <Text style={styles.value}>{hospital_address}</Text>}
            {hospital_email && <Text style={styles.value}>{hospital_email}</Text>}
            {hospital_phone && <Text style={styles.value}>{hospital_phone}</Text>}
          </View>
        </View>

        {/* Subscription Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Subscription Details</Text>
          <View style={styles.row}>
            <Text style={styles.rowLabel}>Plan Type:</Text>
            <Text style={styles.rowValue}>{planName}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.rowLabel}>Billing Period:</Text>
            <Text style={styles.rowValue}>{billingPeriod}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.rowLabel}>Subscription Start:</Text>
            <Text style={styles.rowValue}>{startDateFormatted}</Text>
          </View>
          {subscription_end && (
            <View style={styles.row}>
              <Text style={styles.rowLabel}>Next Renewal Date:</Text>
              <Text style={styles.rowValue}>{endDateFormatted}</Text>
            </View>
          )}
        </View>

        {/* Items Table */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Invoice Items</Text>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCell, { width: '60%' }]}>Description</Text>
              <Text style={[styles.tableCell, { width: '20%', textAlign: 'center' }]}>Period</Text>
              <Text style={[styles.tableCellRight, { width: '20%' }]}>Amount</Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, { width: '60%' }]}>
                {planName} - {billingPeriod} Subscription
              </Text>
              <Text style={[styles.tableCell, { width: '20%', textAlign: 'center' }]}>
                {billingPeriod}
              </Text>
              <Text style={[styles.tableCellRight, { width: '20%' }]}>{formattedSubtotal}</Text>
            </View>
          </View>
        </View>

        {/* Total */}
        <View style={styles.totalSection}>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Subtotal:</Text>
            <Text style={styles.totalValue}>{formattedSubtotal}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>GST (18%):</Text>
            <Text style={styles.totalValue}>{formattedGst}</Text>
          </View>
          <View style={[styles.totalRow, styles.grandTotal]}>
            <Text style={styles.totalLabel}>Total Amount:</Text>
            <Text style={styles.totalValue}>{formattedTotal}</Text>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>

          <Text style={{ marginBottom: 1 }}>
            DentalHub® is a product of Neinus Private Limited. 
          </Text>
          <Text style={{ marginBottom: 1 }}>
             GSTIN: 33AAJCN6811C1ZS 
           </Text>
          <Text style={{ marginBottom: 1 }}>
            Legal Entity: Neinus Private Limited 
          </Text>
          <Text style={{ marginBottom: 5 }}>
            Place of Supply: As per GST Act 
          </Text>
          <Text>Thank you for your business!</Text>
          <Text style={{ marginTop: 5 }}>This is a computer-generated invoice.</Text>
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePDF;
